import type { GalleryModalProps } from "./gallery-modal.type";

import {
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import { useTranslation } from "react-i18next";

import { GalleryModalButton } from "../gallery-modal-button";

import {
  GALLERY_MODAL_CSS_VARIABLES,
  GALLERY_MODAL_ZOOM,
} from "./gallery-modal.constant";
import { useGalleryModal } from "./gallery-modal.hook";

const { Text } = Typography;

export const GalleryModal = <Data = unknown,>({
  items,
  defaultCurrentSlide,
  onCloseClick,
}: GalleryModalProps<Data>) => {
  const { t } = useTranslation();

  const {
    containerRef,
    currentSlide,
    title,
    progress,
    goToPrevious,
    goToNext,
    zoomOut,
    zoomIn,
  } = useGalleryModal({ items, defaultCurrentSlide });

  return (
    <>
      <style>{`.gallery-modal { ${GALLERY_MODAL_CSS_VARIABLES} }`}</style>
      <div ref={containerRef} className="gallery-modal-content">
        <div className="gallery-modal-header">
          <Text ellipsis={{ tooltip: title }} className="gallery-modal-title">
            {title}
          </Text>
          <div className="gallery-modal-close-button">
            <GalleryModalButton
              outlined
              title={t("global:close")}
              icon={<CloseOutlined />}
              onClick={onCloseClick}
            />
          </div>
        </div>
        <div style={currentSlide.style} className="gallery-modal-slide">
          {currentSlide.previewNode ?? currentSlide.node}
        </div>
        <div className="gallery-modal-left">
          <GalleryModalButton
            outlined
            title={t("global:previous")}
            icon={<LeftOutlined />}
            onClick={goToPrevious}
          />
        </div>
        <div className="gallery-modal-right">
          <GalleryModalButton
            outlined
            title={t("global:next")}
            icon={<RightOutlined />}
            onClick={goToNext}
          />
        </div>
        <div className="gallery-modal-footer">
          <div className="gallery-modal-actions">
            <span className="gallery-modal-progress">{progress}</span>
            <GalleryModalButton
              title={t("global:zoomOut")}
              icon={<ZoomOutOutlined />}
              disabled={currentSlide.zoom === GALLERY_MODAL_ZOOM.MIN}
              onClick={zoomOut}
            />
            <GalleryModalButton
              title={t("global:zoomIn")}
              icon={<ZoomInOutlined />}
              disabled={currentSlide.zoom === GALLERY_MODAL_ZOOM.MAX}
              onClick={zoomIn}
            />
          </div>
        </div>
      </div>
    </>
  );
};
